import { twMerge } from 'tailwind-merge';
import { cva } from 'class-variance-authority';
import IconButton from '../IconButton/iconButton';

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  color?: 'default' | 'gray' | 'blue' | 'green' | 'yellow' | 'red';
  type?: 'regular' | 'small';
  dismissible?: boolean;
  dismissLabel?: string;
  className?: string;
  onDismiss?: () => void;
}

const badgeClasses = cva(
  [
    'preflight',
    'ds-rounded-xl',
    'ds-border',
    'ds-px-2',
    'ds-py-1',
    'ds-inline-flex',
    'ds-items-center'
  ],
  {
    variants: {
      color: {
        default: ['ds-border-neutrals-light'],
        gray: ['ds-bg-neutrals-lighter', 'ds-border-neutrals-lighter'],
        blue: [
          'ds-bg-information-blue-lightest',
          'ds-text-information-blue-darkest',
          'ds-border-information-blue-lightest'
        ],
        green: [
          'ds-bg-success-green-lightest',
          'ds-text-success-green-darkest',
          'ds-border-success-green-lightest'
        ],
        yellow: [
          'ds-bg-warning-yellow-lightest',
          'ds-text-warning-yellow-darkest',
          'ds-border-warning-yellow-lightest'
        ],
        red: [
          'ds-bg-danger-red-lightest',
          'ds-text-danger-red-darkest',
          'ds-border-danger-red-lightest'
        ]
      },

      type: {
        regular: ['ds-text-base'],
        small: ['ds-text-sm']
      }
    }
  }
);

const Badge = ({
  label,
  type = 'regular',
  color = 'default',
  dismissible = false,
  dismissLabel = '',
  className = '',
  onDismiss,
  ...props
}: BadgeProps) => {
  const badgeArialLabel = props['aria-label'] || label;

  return (
    <div
      className={twMerge(badgeClasses({ color, type }), className)}
      aria-label={badgeArialLabel}
      {...props}
    >
      <div className="ds-max-w-[200px] ds-truncate ds-leading-6">{label}</div>

      {dismissible && (
        // Still confirming if the background should be transparent or white
        <IconButton
          variant="small"
          iconName="close"
          label={dismissLabel ? dismissLabel : 'dismiss'}
          disabled={false}
          onClick={() => onDismiss && onDismiss()}
          className="ds-ms-1"
          isTransparent={true}
        />
      )}
    </div>
  );
};

export default Badge;
